import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { StarIcon, MapPinIcon, HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';
import { ArrowUpOnSquareIcon, HeartIcon, StarIcon as StarOutlineIcon } from '@heroicons/react/24/outline';
import { resolveImageUrl } from '../../utils/formatters';
import { businessAPI } from '../../api/businesses';

const BusinessCard = ({ business, onFavoriteChanged }) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(!!business?.is_favorite);

  useEffect(() => {
    setIsFavorite(!!business?.is_favorite);
  }, [business?.is_favorite]);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    
    for (let i = 0; i < fullStars; i++) {
      stars.push(<StarIcon key={i} className="h-4 w-4 text-yellow-400" />);
    }
    if (hasHalfStar) {
      stars.push(<StarIcon key="half" className="h-4 w-4 text-yellow-400" />);
    }
    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<StarOutlineIcon key={`empty-${i}`} className="h-4 w-4 text-gray-300" />);
    }
    return stars;
  };

  const getIcon = () => {
    switch(business.business_type) {
      case 'salon': return '💇';
      case 'clinic': return '🏥';
      case 'gym': return '💪';
      case 'ride': return '🚗';
      default: return '🏪';
    }
  };

  const getGradient = () => {
    switch(business.business_type) {
      case 'salon': return 'from-pink-400 to-pink-600';
      case 'clinic': return 'from-blue-400 to-blue-600';
      case 'gym': return 'from-green-400 to-green-600';
      case 'ride': return 'from-purple-400 to-purple-600';
      default: return 'from-gray-400 to-gray-600';
    }
  };

  return (
    <div 
      className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer transform transition-all hover:scale-105 hover:shadow-xl"
      onClick={() => navigate(`/business/${business.id}/book`)}
    >
      <div className="h-32 relative overflow-hidden">
        {business.business_profile_image ? (
          <img
            src={resolveImageUrl(business.business_profile_image)}
            alt={business.business_name}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className={`h-full w-full bg-gradient-to-r ${getGradient()} flex items-center justify-center`}>
            <span className="text-5xl">{getIcon()}</span>
          </div>
        )}
        <div className="absolute top-2 right-2 flex items-center gap-2">
          <button
            type="button"
            className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-1.5 shadow"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const shareUrl = `${window.location.origin}/business/${business.id}/book`;
                if (navigator.share) {
                  await navigator.share({ title: business.business_name, url: shareUrl });
                } else if (navigator.clipboard?.writeText) {
                  await navigator.clipboard.writeText(shareUrl);
                  toast.success('Link copied');
                } else {
                  window.prompt('Copy this link:', shareUrl);
                }
              } catch (err) {
                toast.error('Failed to share');
              }
            }}
          >
            <ArrowUpOnSquareIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="bg-white/90 hover:bg-white text-gray-900 rounded-full p-1.5 shadow"
            onClick={async (e) => {
              e.stopPropagation();
              const next = !isFavorite;
              setIsFavorite(next);
              try {
                if (next) {
                  await businessAPI.addFavorite(business.id);
                  toast.success('Added to favorites');
                } else {
                  await businessAPI.removeFavorite(business.id);
                  toast.success('Removed from favorites');
                }
                if (onFavoriteChanged) onFavoriteChanged(business.id, next);
              } catch (err) {
                setIsFavorite(!next);
              }
            }}
          >
            {isFavorite ? <HeartSolidIcon className="h-4 w-4 text-red-600" /> : <HeartIcon className="h-4 w-4" />}
          </button>
        </div>

        {business.rating >= 4.5 && (
          <div className="absolute top-2 left-2 bg-yellow-400 text-white text-xs font-bold px-2 py-1 rounded-full">
            Top Rated
          </div>
        )}
      </div>
      
      <div className="p-4">
        <h3 className="font-semibold text-lg text-gray-900 mb-1">
          {business.business_name}
        </h3>
        
        <div className="flex items-center mb-2">
          <div className="flex items-center mr-2">
            {renderStars(business.rating)}
          </div>
          <span className="text-sm text-gray-600">
            ({business.review_count || 0} reviews)
          </span>
        </div>
        
        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
          {business.description || 'Professional services tailored to your needs.'}
        </p>
        
        <div className="flex items-center text-sm text-gray-500 mb-1">
          <MapPinIcon className="h-4 w-4 mr-1 flex-shrink-0" />
          <span className="truncate">{business.address}</span>
        </div>
        
        <div className="flex items-center justify-between mt-3 pt-3 border-t">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-primary-600">
              {business.price_range || '$$'}
            </span>
          </div>
          <button 
            className="px-3 py-1 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/business/${business.id}/book`);
            }}
          >
            Book Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default BusinessCard;
